import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Save,
  SlidersHorizontal,
  Star,
  Target,
  XCircle,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeCriteriaItem = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  return String(item.skill || item.name || "").trim();
};

const uniqueByName = (items) => {
  const seen = new Set();

  return items
    .map(normalizeCriteriaItem)
    .filter(Boolean)
    .filter((item) => {
      const key = normalizeText(item);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getCandidateScore = (candidate) => {
  const value = Number(candidate?.match_score || 0);
  if (Number.isNaN(value)) return 0;
  return Math.round(value);
};

const getBreakdownRows = (candidate, field) => {
  const breakdown = candidate?.scoring_breakdown || candidate?.candidate_summary?.scoring_breakdown || {};
  return Array.isArray(breakdown?.[field]) ? breakdown[field] : [];
};

const findSkillBreakdown = (candidate, skill) => {
  const key = normalizeText(skill);
  const rows = [
    ...getBreakdownRows(candidate, "must_have"),
    ...getBreakdownRows(candidate, "preferred"),
  ];

  return rows.find((row) => normalizeText(row?.skill || row?.name) === key);
};

const getSkillSignal = (candidate, skill, threshold) => {
  const key = normalizeText(skill);
  const matched = (candidate?.matching_skills || []).some((item) => normalizeText(item) === key);
  const missing = (candidate?.missing_skills || []).some((item) => normalizeText(item) === key);
  const breakdownRow = findSkillBreakdown(candidate, skill);

  if (breakdownRow) {
    const rawScore = Number(
      breakdownRow.score ??
      breakdownRow.skill_score ??
      breakdownRow.coverage ??
      breakdownRow.match_score ??
      0
    );
    const score = Number.isNaN(rawScore) ? 0 : Math.max(0, Math.min(100, rawScore));

    return {
      known: true,
      passed: score >= threshold,
      score,
    };
  }

  if (matched) {
    return { known: true, passed: true, score: 100 };
  }

  if (missing) {
    return { known: true, passed: false, score: 0 };
  }

  return { known: false, passed: false, score: 0 };
};

export default function JobCriteriaSimulator() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();

  const [jobData, setJobData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [minimumScore, setMinimumScore] = useState(55);
  const [mustThreshold, setMustThreshold] = useState(70);
  const [preferredThreshold, setPreferredThreshold] = useState(50);
  const [requireAllMust, setRequireAllMust] = useState(true);
  const [selectedMustSkills, setSelectedMustSkills] = useState([]);
  const [selectedPreferredSkills, setSelectedPreferredSkills] = useState([]);
  const [savingCriteria, setSavingCriteria] = useState(false);

  useLongActionNotice(savingCriteria, "Save criteria simulation", 30000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || !jobId) return;

    const fetchJobDetails = async () => {
      setFetching(true);

      try {
        const res = await apiFetch(`/jobs/${jobId}/details`);
        const data = await res.json();

        if (!res.ok) {
          toast.error(data?.detail || "Failed to load criteria simulator.");
          navigate("/jobs");
          return;
        }

        setJobData(data);
      } catch (err) {
        toast.error("Failed to load criteria simulator.");
      } finally {
        setFetching(false);
      }
    };

    fetchJobDetails();
  }, [user, jobId, navigate]);

  const job = jobData?.job || {};
  const candidates = Array.isArray(jobData?.candidates) ? jobData.candidates : [];
  const isArchived = Boolean(job.is_archived);

  const jobMustSkills = useMemo(
    () => uniqueByName(job.must_have_skills || []),
    [job.must_have_skills]
  );

  const jobPreferredSkills = useMemo(
    () => uniqueByName(job.preferred_skills || []),
    [job.preferred_skills]
  );

  const allSkillOptions = useMemo(() => {
    const jobDerivedSkills = candidates.flatMap((candidate) => [
      ...(candidate.missing_skills || []),
      ...getBreakdownRows(candidate, "must_have").map((item) => item.skill || item.name),
      ...getBreakdownRows(candidate, "preferred").map((item) => item.skill || item.name),
    ]);

    return uniqueByName([...jobMustSkills, ...jobPreferredSkills, ...jobDerivedSkills]);
  }, [candidates, jobMustSkills, jobPreferredSkills]);

  useEffect(() => {
    if (!jobData) return;

    setSelectedMustSkills((current) => (
      current.length ? current : jobMustSkills.slice(0, 8)
    ));
    setSelectedPreferredSkills((current) => (
      current.length ? current : jobPreferredSkills.slice(0, 8)
    ));
  }, [jobData, jobMustSkills, jobPreferredSkills]);

  const toggleSkill = (skill, type) => {
    const setter = type === "must" ? setSelectedMustSkills : setSelectedPreferredSkills;

    setter((current) => (
      current.some((item) => normalizeText(item) === normalizeText(skill))
        ? current.filter((item) => normalizeText(item) !== normalizeText(skill))
        : [...current, skill]
    ));
  };

  const handleResetControls = () => {
    setMinimumScore(55);
    setMustThreshold(70);
    setPreferredThreshold(50);
    setRequireAllMust(true);
    setSelectedMustSkills(jobMustSkills.slice(0, 8));
    setSelectedPreferredSkills(jobPreferredSkills.slice(0, 8));
  };

  const handleSaveCriteria = async () => {
    if (isArchived) {
      toast.error("Restore this job before saving criteria.");
      return;
    }

    const skillThresholds = selectedMustSkills.reduce((acc, skill) => {
      acc[skill] = mustThreshold;
      return acc;
    }, {});

    setSavingCriteria(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/scoring-criteria`, {
        method: "PATCH",
        body: JSON.stringify({
          must_have_skills: selectedMustSkills.map((skill) => ({
            skill,
            threshold: mustThreshold,
          })),
          preferred_skills: selectedPreferredSkills.map((skill) => ({ skill })),
          skill_thresholds: skillThresholds,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Save criteria simulation", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        job: {
          ...(prev?.job || {}),
          must_have_skills: data.must_have_skills || [],
          preferred_skills: data.preferred_skills || [],
          skill_thresholds: data.skill_thresholds || {},
        },
      }));
      toast.success("Criteria saved. New scans and rescans will use this setup.");
    } catch (err) {
      toast.error(actionFailureMessage("Save criteria simulation"));
    } finally {
      setSavingCriteria(false);
    }
  };

  const handleReviewReadyCandidates = () => {
    const readyScanIds = simulation.readyRows
      .map((row) => row.candidate.scan_id)
      .filter(Boolean);

    if (!readyScanIds.length) {
      toast.error("No candidates pass the current simulation.");
      return;
    }

    navigate(`/jobs/${jobId}/rankings?ready_scan_ids=${readyScanIds.join(",")}`);
  };

  const simulation = useMemo(() => {
    const rows = candidates.map((candidate) => {
      const score = getCandidateScore(candidate);
      const missingMust = selectedMustSkills.filter((skill) => (
        !getSkillSignal(candidate, skill, mustThreshold).passed
      ));
      const unknownMust = selectedMustSkills.filter((skill) => (
        !getSkillSignal(candidate, skill, mustThreshold).known
      ));
      const matchedPreferred = selectedPreferredSkills.filter((skill) => (
        getSkillSignal(candidate, skill, preferredThreshold).passed
      ));
      const scorePassed = score >= minimumScore;
      const mustPassed = !requireAllMust || missingMust.length === 0;
      const passes = scorePassed && mustPassed;

      let status = "Ready to review";
      if (!passes && missingMust.length) status = "Blocked by must-have criteria";
      else if (!passes) status = "Below score target";

      return {
        candidate,
        score,
        passes,
        status,
        missingMust,
        unknownMust,
        matchedPreferred,
      };
    });

    const readyRows = rows.filter((row) => row.passes);
    const blockedRows = rows.filter((row) => !row.passes);
    const gapCounts = selectedMustSkills.map((skill) => {
      const matched = candidates.filter((candidate) => (
        getSkillSignal(candidate, skill, mustThreshold).passed
      )).length;
      const unknown = candidates.filter((candidate) => (
        !getSkillSignal(candidate, skill, mustThreshold).known
      )).length;
      const missing = candidates.length - matched - unknown;

      return { skill, matched, missing, unknown };
    });

    const topGap = [...gapCounts].sort((a, b) => (
      (b.missing + b.unknown) - (a.missing + a.unknown)
    ))[0];

    return {
      rows,
      readyRows,
      blockedRows,
      gapCounts,
      topGap,
    };
  }, [candidates, minimumScore, mustThreshold, preferredThreshold, requireAllMust, selectedMustSkills, selectedPreferredSkills]);

  const poolHealth = useMemo(() => {
    if (!candidates.length) return { label: "No scan data", tone: "slate" };
    const ratio = simulation.readyRows.length / candidates.length;

    if (ratio >= 0.25) return { label: "Healthy shortlist pool", tone: "emerald" };
    if (ratio >= 0.08) return { label: "Narrow but usable", tone: "amber" };
    return { label: "Criteria may be too strict", tone: "rose" };
  }, [candidates.length, simulation.readyRows.length]);

  const recommendations = useMemo(() => {
    if (!candidates.length) {
      return ["Scan candidates for this job first, then use the simulator to inspect hiring bottlenecks."];
    }

    const items = [];

    if (simulation.readyRows.length === 0) {
      items.push("No candidate passes the current setup. Lower the score target or review must-have requirements.");
    }

    if (simulation.topGap?.skill) {
      const affected = simulation.topGap.missing + simulation.topGap.unknown;
      if (affected > 0) {
        items.push(`${simulation.topGap.skill} is the biggest bottleneck across this job's scanned candidates.`);
      }
    }

    if (selectedMustSkills.length === 0) {
      items.push("Add must-have skills to see which requirements block candidates.");
    }

    if (!items.length) {
      items.push("The current criteria produce a usable review pool. Open Rankings to inspect the strongest candidates.");
    }

    return items;
  }, [candidates.length, selectedMustSkills.length, simulation.readyRows.length, simulation.topGap]);

  if (loading || fetching || !user) {
    return <p className="text-center mt-20">Loading...</p>;
  }

  const hasScannedCandidates = candidates.length > 0;

  const leftPanel = (
    <div className="flex flex-col gap-2">
      <h3 className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-emerald-300">
        Job Workspace
      </h3>

      <button
        onClick={() => navigate(`/jobs/${jobId}`)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white"
      >
        <ArrowLeft size={16} />
        Job Details
      </button>

      <button
        onClick={() => navigate(`/jobs/${jobId}/rankings`)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white"
      >
        <Star size={16} />
        Rankings
      </button>

      <button
        onClick={() => navigate(`/jobs/${jobId}/criteria-simulator`)}
        className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-emerald-900"
      >
        <Target size={16} />
        Criteria Simulator
      </button>
    </div>
  );

  const emptyStateContent = (
    <section className="rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-700" />
            <h2 className="text-xl font-semibold text-amber-950">Scan candidates before using the simulator</h2>
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-amber-800">
            Criteria Simulator needs scanned candidate results to show who becomes ready, which skills are blocking candidates, and how criteria changes affect this job.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(`/bulk-scan?job_id=${jobId}`)}
          className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900"
        >
          Run Bulk Scan
        </button>
      </div>
    </section>
  );

  const mainContent = !hasScannedCandidates ? emptyStateContent : (
    <>
      {isArchived && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
          This job is archived. The simulator is read-only and uses the existing scan history.
        </section>
      )}

      <section className="rounded-lg border border-emerald-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={20} className="text-emerald-700" />
              <h2 className="text-xl font-semibold text-slate-950">Recruiter Criteria Simulator</h2>
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Test how strict requirements affect this job before deciding who to review. This does not rescan candidates or spend AI tokens.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              onClick={handleSaveCriteria}
              disabled={savingCriteria || isArchived}
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save size={16} />
              {savingCriteria ? "Saving..." : "Save Criteria"}
            </button>

            <button
              onClick={handleReviewReadyCandidates}
              disabled={!simulation.readyRows.length}
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Star size={16} />
              Review Ready Candidates
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-4">
          <label className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Minimum score</span>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={minimumScore}
                onChange={(event) => setMinimumScore(Number(event.target.value))}
                className="w-full accent-emerald-700"
              />
              <span className="w-12 text-right text-lg font-semibold text-slate-950">{minimumScore}%</span>
            </div>
          </label>

          <label className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Must-have threshold</span>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={mustThreshold}
                onChange={(event) => setMustThreshold(Number(event.target.value))}
                className="w-full accent-emerald-700"
              />
              <span className="w-12 text-right text-lg font-semibold text-slate-950">{mustThreshold}%</span>
            </div>
          </label>

          <label className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preferred threshold</span>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={preferredThreshold}
                onChange={(event) => setPreferredThreshold(Number(event.target.value))}
                className="w-full accent-emerald-700"
              />
              <span className="w-12 text-right text-lg font-semibold text-slate-950">{preferredThreshold}%</span>
            </div>
          </label>

          <label className="flex min-h-[116px] cursor-pointer items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Strict must-have mode</span>
              <span className="mt-2 block text-sm leading-5 text-slate-600">
                Require every selected must-have skill to pass.
              </span>
            </span>
            <input
              type="checkbox"
              checked={requireAllMust}
              onChange={(event) => setRequireAllMust(event.target.checked)}
              className="h-5 w-5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-600"
            />
          </label>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <Target size={18} className="text-emerald-700" />
            <h2 className="text-lg font-semibold text-slate-950">Skill Controls</h2>
          </div>
          <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <p className="text-sm text-slate-500">
              Choose which skills should be treated as hiring blockers or useful bonuses for this simulation.
            </p>
            <button
              type="button"
              onClick={handleResetControls}
              className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Reset
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm leading-6 text-emerald-800">
            These chips represent job requirement skills only. They come from saved job criteria and JD-based scan signals, not from unrelated skills found only in candidate CVs.
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">Must-have skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {allSkillOptions.length ? allSkillOptions.map((skill) => {
                  const selected = selectedMustSkills.some((item) => normalizeText(item) === normalizeText(skill));

                  return (
                    <button
                      key={`must-${skill}`}
                      type="button"
                      onClick={() => toggleSkill(skill, "must")}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        selected
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-500 hover:border-emerald-200 hover:text-emerald-700"
                      }`}
                    >
                      {skill}
                    </button>
                  );
                }) : (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    No skills available yet.
                  </p>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-800">Preferred skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {allSkillOptions.length ? allSkillOptions.map((skill) => {
                  const selected = selectedPreferredSkills.some((item) => normalizeText(item) === normalizeText(skill));

                  return (
                    <button
                      key={`preferred-${skill}`}
                      type="button"
                      onClick={() => toggleSkill(skill, "preferred")}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                        selected
                          ? "border-sky-300 bg-sky-50 text-sky-800"
                          : "border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:text-sky-700"
                      }`}
                    >
                      {skill}
                    </button>
                  );
                }) : (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    No skills available yet.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-300">Decision preview</p>
          <h2 className="mt-2 text-2xl font-semibold">{poolHealth.label}</h2>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-300">Ready</p>
              <p className="mt-2 text-2xl font-semibold">{simulation.readyRows.length}</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-300">Blocked</p>
              <p className="mt-2 text-2xl font-semibold">{simulation.blockedRows.length}</p>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            {recommendations.map((item) => (
              <p key={item} className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm leading-5 text-slate-100">
                {item}
              </p>
            ))}
          </div>
          <div className="mt-5 grid gap-2">
            <button
              type="button"
              onClick={handleReviewReadyCandidates}
              disabled={!simulation.readyRows.length}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-white px-4 text-sm font-semibold text-slate-950 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Review {simulation.readyRows.length} Ready Candidate{simulation.readyRows.length === 1 ? "" : "s"}
            </button>
            <button
              type="button"
              onClick={handleSaveCriteria}
              disabled={savingCriteria || isArchived}
              className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-white/15 px-4 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingCriteria ? "Saving Criteria..." : "Save Criteria to Job"}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-emerald-700" />
          <h2 className="text-lg font-semibold text-slate-950">Bottleneck Detector</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Shows which selected must-have skills reduce the shortlist pool.
        </p>

        <div className="mt-5 space-y-3">
          {simulation.gapCounts.length ? simulation.gapCounts.map((item) => {
            const total = Math.max(1, candidates.length);
            const matchedWidth = Math.round((item.matched / total) * 100);
            const missingWidth = Math.round((item.missing / total) * 100);
            const unknownWidth = Math.max(0, 100 - matchedWidth - missingWidth);

            return (
              <div key={item.skill} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="font-semibold text-slate-950">{item.skill}</p>
                  <p className="text-xs font-medium text-slate-500">
                    {item.matched} matched / {item.missing} missing / {item.unknown} unclear
                  </p>
                </div>
                <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-slate-200">
                  <div className="bg-emerald-500" style={{ width: `${matchedWidth}%` }} />
                  <div className="bg-rose-400" style={{ width: `${missingWidth}%` }} />
                  <div className="bg-slate-300" style={{ width: `${unknownWidth}%` }} />
                </div>
              </div>
            );
          }) : (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
              Select at least one must-have skill to detect bottlenecks.
            </p>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Candidate Impact</h2>
            <p className="mt-1 text-sm text-slate-500">
              This is a simulation from existing scan data. It does not change rankings or shortlist status.
            </p>
          </div>
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
            poolHealth.tone === "emerald"
              ? "bg-emerald-50 text-emerald-700"
              : poolHealth.tone === "amber"
              ? "bg-amber-50 text-amber-700"
              : "bg-rose-50 text-rose-700"
          }`}>
            {poolHealth.label}
          </span>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">Candidate</th>
                <th className="py-3 pr-4">Score</th>
                <th className="py-3 pr-4">Simulation Status</th>
                <th className="py-3 pr-4">Blocking Gaps</th>
                <th className="py-3 pr-4">Preferred Matched</th>
              </tr>
            </thead>
            <tbody>
              {simulation.rows.map((row) => (
                <tr key={row.candidate.scan_id || row.candidate.id || row.candidate.filename} className="border-b border-slate-100 last:border-0">
                  <td className="max-w-[260px] py-3 pr-4 font-semibold text-slate-950">
                    <span className="block truncate">{row.candidate.filename}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700">
                      {row.score}%
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                      row.passes
                        ? "bg-emerald-50 text-emerald-700"
                        : row.missingMust.length
                        ? "bg-rose-50 text-rose-700"
                        : "bg-amber-50 text-amber-700"
                    }`}>
                      {row.passes ? <CheckCircle2 size={13} /> : row.missingMust.length ? <XCircle size={13} /> : <AlertTriangle size={13} />}
                      {row.status}
                    </span>
                  </td>
                  <td className="max-w-[300px] py-3 pr-4 text-slate-600">
                    <span className="block truncate">{row.missingMust.join(", ") || "-"}</span>
                  </td>
                  <td className="max-w-[260px] py-3 pr-4 text-slate-600">
                    <span className="block truncate">{row.matchedPreferred.join(", ") || "-"}</span>
                  </td>
                </tr>
              ))}
              {!simulation.rows.length && (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-sm text-slate-500">
                    No scanned candidates are available for this job yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Criteria simulator"
      title={job.name ? `${job.name} Criteria Simulator` : "Criteria Simulator"}
      subtitle="Preview how recruiter-defined requirements change the review pool before taking action."
      leftPanel={leftPanel}
      mainContent={mainContent}
      headerAction={
        <button
          onClick={() => navigate(`/jobs/${jobId}`)}
          className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          <ArrowLeft size={16} />
          Job Details
        </button>
      }
    />
  );
}
